<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { getIconClasses } from "@/helpers/cssClass";
import { formatDate, formatHour } from "@/helpers/date";
import { useConfigurationStore } from "@/store/configurationStore";

import type { MissionModalData } from "@/modules/companyNeed/types/missions";

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import AsideSectionTitle from "@/dsComponents/modals/subcomponents/AsideSectionTitle.vue";
import Separator from "@/dsComponents/Separator.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import DateFormated from "@/components/dateFormatted/DateFormated.vue";
import HourFormated from "@/components/Mission/HourFormated.vue";

interface MissionDetailsTabProps {
    missionData: MissionModalData;
}

interface AsideItems {
    label: string;
    icon?: string;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    value: any;
    iconColor?: "yellow" | "green" | "blue";
    valueClass?: string;
}

const DATE_FORMAT = "ddd DD MMM YY";

const props = defineProps<MissionDetailsTabProps>();
const configStore = useConfigurationStore();

const { t } = useI18n();

const columnsDateTable = [{ field: "day", width: "220px" }, { field: "hour" }];

const getPrincipalInfo = computed((): (AsideItems & { dataAttribute: string })[] => {
    const values: (AsideItems & { dataAttribute: string })[] = [];

    if (props.missionData.address.city) {
        values.push({
            label: t("modals.missionDetails.details.label.location"),
            icon: "map-pin-circle",
            value: props.missionData.address.city,
            dataAttribute: "mission-location",
        });
    }

    if (props.missionData.contractType) {
        values.push({
            label: t("modals.missionDetails.details.label.contract"),
            icon: "signature",
            value: props.missionData.contractType,
            dataAttribute: "mission-contract-type",
        });
    }

    if (props.missionData.totalPositions) {
        values.push({
            label: t("modals.missionDetails.details.label.workers"),
            icon: "users",
            value: props.missionData.totalPositions.toString(),
            dataAttribute: "mission-worker-needed",
        });
    }
    if (props.missionData.remuneration) {
        const minAmount = (props.missionData.remuneration.minimumAmount ?? 0).toString();
        const maxAmount = (props.missionData.remuneration.maximumAmount ?? 0).toString();
        values.push({
            label: t("modals.missionDetails.details.remuneration.label"),
            icon: "cash",
            value: t("modals.missionDetails.details.remuneration.value", {
                minAmount,
                maxAmount,
                periodicity:
                    configStore.remunerationPeriodicityOptions.find((option) => option.value === props.missionData.remuneration?.periodicity)
                        ?.label ?? props.missionData.remuneration.periodicity,
            }),
            dataAttribute: "mission-remuneration",
        });
    }

    if (props.missionData.hiringReason) {
        const hiringReasonLabel = configStore.hiringReasonOptions.find((option) => option.value === props.missionData.hiringReason)?.label;

        values.push({
            label: t("modals.missionDetails.details.label.hiringReason"),
            icon: "selector",
            value: hiringReasonLabel,
            dataAttribute: "mission-hiring-reason",
        });
    }

    return values;
});

const formattedDates = computed(() => {
    return props.missionData.periods.map((period) => ({
        content: {
            day: {
                customElement: {
                    component: DateFormated,
                    props: {
                        startDate: formatDate(period.start, DATE_FORMAT),
                        endDate: formatDate(period.end, DATE_FORMAT),
                        icon: "arrow-right",
                    },
                },
            },
            hour: {
                customElement: {
                    component: HourFormated,
                    props: {
                        from: formatHour(period.start),
                        to: formatHour(period.end),
                        icon: "arrow-right",
                    },
                },
            },
        },
        editMode: false,
    }));
});
</script>

<template>
    <div class="details-tab">
        <div class="mission-infos">
            <div class="principal-info">
                <div class="aside-section-form">
                    <div class="aside-items">
                        <div class="side-modal-label typo-xxs-medium">
                            <i :class="['aside-icon typo-sm-regular', ...getIconClasses('write')]" />
                            <span>{{ t("modals.missionDetails.details.label.creation") }}</span>
                        </div>
                        <div class="side-modal-value-text typo-xxs-regular">
                            <span data-bot="mission-creation-date">
                                {{
                                    t("modals.missionDetails.details.creation", {
                                        date: formatDate(missionData.createdAt, DATE_FORMAT),
                                        firstname: missionData.createdBy.firstname,
                                        lastname: missionData.createdBy.lastname,
                                    })
                                }}
                            </span>
                        </div>
                    </div>
                    <div v-for="(item, index) in getPrincipalInfo" :key="index" class="aside-items">
                        <div class="side-modal-label typo-xxs-medium" :class="item.valueClass">
                            <i v-if="item.icon" :class="['aside-icon typo-sm-regular', item.iconColor, ...getIconClasses(item.icon)]" />
                            <span>{{ item.label }}</span>
                        </div>
                        <div class="side-modal-value">
                            <ComboButton class="label-combo" readonly noInnerPadding :data-bot="item.dataAttribute">
                                <span>
                                    {{ item.value }}
                                </span>
                            </ComboButton>
                        </div>
                    </div>
                    <div class="aside-items" v-if="missionData.explanation">
                        <div class="side-modal-label typo-xxs-medium">
                            <i :class="['aside-icon typo-sm-regular', ...getIconClasses('answer-text')]" />
                            <span>{{ t("modals.missionDetails.details.label.explanation") }}</span>
                        </div>
                        <div>
                            <TextareaInput :modelValue="missionData.explanation ?? ''" readOnly noBorder data-bot="mission-explanation" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Separator />

        <div class="mission-date">
            <AsideSectionTitle :title="t('modals.missionDetails.details.label.dates')" disableOpenClose />
            <div class="dates-table">
                <TableAlt :columns="columnsDateTable" :rows="formattedDates" />
            </div>
        </div>

        <Separator />

        <div class="mission-describe">
            <AsideSectionTitle :title="t('modals.missionDetails.details.label.missionDescription')" disableOpenClose />
            <div class="describe-content">
                <pre class="typo-xs-medium">{{ missionData.description ?? "-" }}</pre>
                <div v-if="missionData.note" class="note">
                    <span class="typo-xxs-medium">{{ t("modals.missionDetails.details.label.note") }}</span>
                    <pre class="typo-xs-medium">{{ missionData.note }}</pre>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.aside-section-form {
    gap: var(--spacing-md, 8px);
    display: flex;
    flex-direction: column;

    .aside-items {
        display: flex;
        align-items: flex-start;
        min-height: 28px;
        padding-left: var(--spacing-lg, 12px);

        .side-modal-label {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            gap: var(--spacing-sm, 6px);
            width: 128px;
            height: 28px;
            color: var(--text-sub-600, #494e61);

            .aside-icon {
                flex-shrink: 0;
                width: 14px;
                height: 14px;
                font-size: 14px;
                color: var(--text-sub-600, #494e61);

                &.yellow {
                    color: var(--yellow-base, #f59e0b);
                }

                &.green {
                    color: var(--green-base, #00a355);
                }

                &.blue {
                    color: var(--blue-base, #2051f8);
                }
            }
        }

        .side-modal-value {
            height: 28px;
            width: 100%;
            overflow: hidden;

            &-text {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                align-self: stretch;
                padding-left: var(--spacing-md, 8px);

                color: var(--text-sub-600, #494e61);
            }

            &-textarea {
                height: 100%;
                width: 100%;
                padding: 0 var(--spacing-md, 8px);
            }

            .label-combo {
                flex: 1;
                overflow: hidden;
            }
        }
    }
}

.details-tab {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3xl, 24px);
    height: 100%;
    padding: var(--spacing-3xl, 24px) var(--spacing-lg, 12px) var(--spacing-3xl, 24px) 0;
    overflow: auto;
}

.mission-infos {
    padding: 0 var(--spacing-lg, 12px);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);
}

.mission-date {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);
    padding: 0 var(--spacing-lg, 12px);

    h2 {
        padding: 0 18px;
        color: var(--neutral-700, #323848);
    }

    .dates-table {
        padding: 0 var(--spacing-sm, 6px);
    }
}

.mission-describe {
    gap: var(--spacing-xl, 16px);
    display: flex;
    padding: 0 var(--spacing-lg, 12px);
    flex-direction: column;

    h2 {
        padding: 0 var(--spacing-lg, 12px);
        color: var(--neutral-700, #323848);
    }

    .describe-content {
        padding: 0 var(--spacing-lg, 12px);

        pre {
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            max-width: 100%;
            margin-bottom: 30px;
        }

        .note {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-xs, 4px);

            span {
                color: var(--text-sub-600, #4c505e);
            }

            pre {
                white-space: pre-wrap;
                word-wrap: break-word;
                overflow-wrap: break-word;
                max-width: 100%;
                margin: 0;
            }
        }
    }
}
</style>
